import { createEffectHook, TypedEvent } from '@rehaje/core'

import type { SchemaModel, SchemaNode, ValuableNode } from './models'
import type { ValuableModel } from './models/ValuableModel'
import type { FormFeedback, SchemaMessages } from './types'
import type { ViewElement } from '@rehaje/core'

export type FormTarget = ViewElement<SchemaNode, SchemaModel>

export type FormValueTarget = ViewElement<ValuableNode, ValuableModel>
/**
 * 失焦事件
 */
export class FormBlurEvent extends TypedEvent<
  FormValueTarget,
  unknown,
  FormTarget
> {
  override type = 'onFormBlur' as const
  constructor(public componentEvent?: Event) {
    super()
  }
}
/**
 * 焦点事件
 */
export class FormFocusEvent extends TypedEvent<
  FormValueTarget,
  unknown,
  FormTarget
> {
  override type = 'onFormFocus' as const
  constructor(public componentEvent?: Event) {
    super()
  }
}

/**
 * 表单或字段默认值变化事件
 */
export class FormInitialValueChangeEvent extends TypedEvent<
  FormValueTarget,
  unknown,
  FormTarget
> {
  override type = 'onFormInitialValueChange' as const
}

/**
 * 输入事件，支持阻止默认行为
 */
export class FormInputEvent extends TypedEvent<
  FormValueTarget,
  unknown,
  FormTarget
> {
  override type = 'onFormInput' as const
  constructor(
    values: any[],
    public componentEvent?: InputEvent
  ) {
    super(values)
  }
}

/**
 * 重置事件，支持阻止默认行为
 */
export class FormResetEvent extends TypedEvent<
  FormValueTarget,
  unknown,
  FormTarget
> {
  override type = 'onFormReset' as const
}
/**
 * 提交错误事件
 */
export class FormSubmitErrorEvent extends TypedEvent<
  FormValueTarget,
  FormFeedback[],
  FormTarget
> {
  override type = 'onFormSubmitError' as const
}
/**
 * 提交事件，支持阻止默认行为
 */
export class FormSubmitEvent extends TypedEvent<
  FormValueTarget,
  unknown,
  FormTarget
> {
  override type = 'onFormSubmit' as const
}

/**
 * 提交成功事件
 */
export class FormSubmitSuccessEvent extends TypedEvent<
  FormValueTarget,
  unknown,
  FormTarget
> {
  override type = 'onFormSubmitSuccess' as const
}

/**
 * 校验事件
 */
export class FormValidateEvent extends TypedEvent<
  FormValueTarget,
  SchemaMessages[],
  FormTarget
> {
  override type = 'onFormValidate' as const
}

/**
 * 表单或字段值变化事件
 */
export class FormValueChangeEvent extends TypedEvent<
  FormValueTarget,
  { oldValue?: any; value: any },
  FormTarget
> {
  override type = 'onFormValueChange' as const
}

export const { onFormValueChange, onFormValueChangeCapture } =
  createEffectHook(FormValueChangeEvent)

export const { onFormInitialValueChange, onFormInitialValueChangeCapture } =
  createEffectHook(FormInitialValueChangeEvent)

export const { onFormReset, onFormResetCapture } =
  createEffectHook(FormResetEvent)

export const { onFormSubmit, onFormSubmitCapture } =
  createEffectHook(FormSubmitEvent)

export const { onFormSubmitSuccess, onFormSubmitSuccessCapture } =
  createEffectHook(FormSubmitSuccessEvent)

export const { onFormSubmitError, onFormSubmitErrorCapture } =
  createEffectHook(FormSubmitErrorEvent)

export const { onFormInput, onFormInputCapture } =
  createEffectHook(FormInputEvent)

export const { onFormFocus, onFormFocusCapture } =
  createEffectHook(FormFocusEvent)

export const { onFormBlur, onFormBlurCapture } = createEffectHook(FormBlurEvent)

export const { onFormValidate, onFormValidateCapture } =
  createEffectHook(FormValidateEvent)

declare global {
  export interface RehajeTypedEvents {
    onFormBlur: FormBlurEvent
    onFormBlurCapture: FormBlurEvent
    onFormFocus: FormFocusEvent
    onFormFocusCapture: FormFocusEvent
    onFormInitialValueChange: FormInitialValueChangeEvent
    onFormInitialValueChangeCapture: FormInitialValueChangeEvent
    onFormInput: FormInputEvent
    onFormInputCapture: FormInputEvent
    onFormReset: FormResetEvent
    onFormResetCapture: FormResetEvent
    onFormSubmit: FormSubmitEvent
    onFormSubmitCapture: FormSubmitEvent
    onFormSubmitError: FormSubmitErrorEvent
    onFormSubmitErrorCapture: FormSubmitErrorEvent
    onFormSubmitSuccess: FormSubmitSuccessEvent
    onFormSubmitSuccessCapture: FormSubmitSuccessEvent
    onFormValidate: FormValidateEvent
    onFormValidateCapture: FormValidateEvent
    onFormValueChange: FormValueChangeEvent
    onFormValueChangeCapture: FormValueChangeEvent
  }
}
